import { LOCAL_STORE_NAMESPACE } from '@streamlain/doc-publication-contract';
import { type IDBPDatabase, openDB } from 'idb';

import { AutoReconnectConnection } from '../../connection';
import { localStoreOpenError } from '../../local-data/account-key-store';
import type { AuthBoundStorageOptions } from '../../storage/local-data-access';
import type { SpaceType } from '../../utils/universal-id';
import { raiseMirror } from './account-mirrors';
import { IDBEncryption } from './encryption';
import { type DocStorageSchema, migrator } from './schema';

export interface IDBConnectionOptions extends AuthBoundStorageOptions {
  flavour: string;
  type: SpaceType;
  id: string;
}

/** Producer input; resolveStoreOptions binds it before any current IDB storage opens. */
export type UnboundIDBConnectionOptions = Omit<
  IDBConnectionOptions,
  'authBinding'
>;

export class IDBConnection extends AutoReconnectConnection<{
  db: IDBPDatabase<DocStorageSchema>;
  channel: BroadcastChannel;
}> {
  readonly encryption = new IDBEncryption(this.opts, this);

  readonly dbName = `${this.opts.flavour}:${this.opts.type}:${this.opts.id}@${LOCAL_STORE_NAMESPACE}:${this.opts.authBinding?.accountId ?? 'local'}`;

  override get shareId() {
    const binding = this.opts.authBinding;
    return JSON.stringify([
      'idb',
      migrator.version,
      this.dbName,
      binding === undefined
        ? 'unset'
        : binding === null
          ? 'local'
          : [binding.accountId, binding.authEpoch],
    ]);
  }

  constructor(private readonly opts: IDBConnectionOptions) {
    super();
  }

  override async doConnect() {
    // Only a validated bound identity may mutate the persistent auth mirror.
    // Unset/null identity probes still connect without a mirror; operations fail closed.
    if (this.opts.authBinding) this.encryption.validate();
    // indexeddb will responsible for version control, so the db.version always match migrator.version
    const db = await openDB<DocStorageSchema>(this.dbName, migrator.version, {
      upgrade: migrator.migrate,
    }).catch(error => {
      throw localStoreOpenError(error);
    });
    const binding = this.opts.authBinding;
    if (binding) {
      const tx = db.transaction('authState', 'readwrite');
      const done = tx.done;
      void done.catch(() => {});
      try {
        const stored = await tx.store.get(binding.accountId);
        // A mirror raised past this binding locks the connection like the
        // account record would.
        const superseded =
          !!stored &&
          (stored.authEpoch > binding.authEpoch ||
            (stored.authEpoch === binding.authEpoch && stored.logoutPending));
        if (!stored || stored.authEpoch < binding.authEpoch) {
          await tx.store.put({
            accountId: binding.accountId,
            authEpoch: binding.authEpoch,
            logoutPending: false,
          });
        }
        await done;
        // The one primary account read of this connection.
        await this.encryption.provider?.connect(
          authority => raiseMirror(db, binding.accountId, authority),
          superseded
        );
      } catch (error) {
        db.close();
        throw error;
      }
    }
    db.addEventListener('versionchange', this.handleVersionChange);

    return {
      db,
      channel: new BroadcastChannel('idb:' + this.dbName),
    };
  }

  override doDisconnect(db: {
    db: IDBPDatabase<DocStorageSchema>;
    channel: BroadcastChannel;
  }) {
    this.encryption.dispose();
    db.db.removeEventListener('versionchange', this.handleVersionChange);
    db.channel.close();
    db.db.close();
  }

  handleVersionChange = (e: IDBVersionChangeEvent) => {
    if (e.newVersion !== migrator.version) {
      this.error = new Error(
        'Database version mismatch, expected ' +
          migrator.version +
          ' but got ' +
          e.newVersion
      );
    }
  };
}
